<template>
  <d-container
    fluid
    :class="{
      'main-content-container': true,
      'px-4': !isOrderView,
      'px-0': isOrderView
    }">
    <!-- Page Header -->
    <d-row
      no-gutters
      :class="{
        'page-header': true,
        'py-4': !isOrderView,
        'mb-4 pb-3 border-bottom': isOrderView
      }">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span
          v-if="!isOrderView"
          class="text-uppercase page-subtitle">
          {{ $t('notifications.notificationsLabel') }}
        </span>
        <h3
          v-if="isDashboardList"
          class="page-title">{{ $t('notifications.notificationsNearest') }}</h3>
        <h3
          v-else-if="isOrderView"
          class="page-title">{{ $t('notifications.notificationsLabel') }}</h3>
        <h3
          v-else
          class="page-title">{{ $t('notifications.notifications') }}</h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          v-if="isDashboardList"
          theme="secondary"
          @click.prevent="$router.push('/powiadomienia')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('notifications.goToFullList') }}
        </d-button>

        <d-button
          theme="success"
          @click.prevent="isOrderView ? $bus.$emit('show-order-notification-new-popup') : $router.push('/powiadomienia/nowe')">
          <i class="material-icons">add</i>
          {{ $t('notifications.addNewNotification') }}
        </d-button>
      </d-col>
    </d-row>

    <list-filters
      v-if="!isDashboardList && !isOrderView"
      class="mb-3"
      :config="filtersConfig"
      :isListLoaded="loaded"
      :view-name="'notifications'"
      @init="handleFiltersInit"
      @search="handleFiltersSearch" />

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0">{{ $t('notifications.description') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 120px;">{{ $t('notifications.dateTime') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 120px;">{{ $t('notifications.form.isRepeatable') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 80px;">{{ $t('notifications.showInDashboard') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 80px;">{{ $t('notifications.sendReminder') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 130px;">{{ $t('notifications.reminderBefore') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 200px;">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <template v-for="(notification, index) of notifications">
            <tr :key="'notification-' + index">
              <td class="align-middle">
                {{ notification.description }}
                <template v-if="userIsAdmin && notification.user">
                  <br>
                  <small class="pt-2 d-block">
                    {{ $t('users.user') }}:
                    <strong>{{ notification.user.name }}</strong>
                  </small>
                  <template v-if="notification.order">
                    <small>
                      {{ $t('notifications.order') }}:
                      <strong>{{ notification.order.order_name }}</strong>
                    </small>
                  </template>
                </template>

                <a
                  v-if="notification.children.length"
                  href="#"
                  @click.prevent="toggleChildrenNotifications(index)">
                  <small v-if="showChildrenNotifications[index]">
                    {{ $t('notifications.hideChildrenNotifications') }} ({{ notification.children.length }})
                  </small>
                  <small v-else>
                    {{ $t('notifications.showChildrenNotifications') }} ({{ notification.children.length }})
                  </small>
                </a>
              </td>

              <td class="align-middle">
                {{ notification.date }}<br>
                {{ notification.time }}
              </td>

              <td class="align-middle">
                <span
                  v-if="notification.is_repeatable"
                  class="text-success">
                  {{ $t('ui.yes') }}
                </span>
                <span
                  v-else
                  class="text-danger">
                  {{ $t('ui.no') }}
                </span>

                <small
                  v-if="notification.is_repeatable"
                  class="d-block">
                  {{ $t('notifications.form.frequencyValues.' + (notification.frequency).toString().toLowerCase()) }}<br>
                  <strong v-if="notification.repeat_number === 0">
                    &infin;
                  </strong>
                  <strong v-else>
                    {{ notification.repeat_number }}
                  </strong> {{ $t('notifications.times') }}
                </small>
              </td>

              <td class="align-middle">
                <span
                  v-if="notification.show_in_dashboard"
                  class="text-success">
                  {{ $t('ui.yes') }}
                </span>
                <span
                  v-if="!notification.show_in_dashboard"
                  class="text-danger">
                  {{ $t('ui.no') }}
                </span>
              </td>

              <td class="align-middle">
                <span
                  v-if="notification.send_reminder"
                  class="text-success">
                  {{ $t('ui.yes') }}
                </span>
                <span
                  v-if="!notification.send_reminder"
                  class="text-danger">
                  {{ $t('ui.no') }}
                </span>
              </td>

              <td class="align-middle">
                {{ parseInt(notification.reminder_before, 10) }} h
              </td>

              <td
                class="align-middle"
                style="width: 150px;">
                <d-button
                  v-if="!notification.is_completed"
                  theme="success"
                  :outline="true"
                  @click.native="markAsCompleted(notification.id)"
                  :disabled="markingInProgress !== false"
                  :title="$t('notifications.markAsCompleted')">
                  <div
                    v-if="markingInProgress === notification.id"
                    class="spinner-border spinner-border-sm"
                    role="status"></div>
                  <i
                    v-else
                    class="material-icons">check</i>
                </d-button>
                <d-button
                  v-if="notification.is_completed"
                  theme="success"
                  @click.native="markAsNotCompleted(notification.id)"
                  :disabled="markingInProgress !== false"
                  :title="$t('notifications.markAsNotCompleted')">
                  <div
                    v-if="markingInProgress === notification.id"
                    class="spinner-border spinner-border-sm"
                    role="status"></div>
                  <i
                    v-else
                    class="material-icons">check</i>
                </d-button>
                <d-button
                  theme="primary"
                  @click.native="$router.push('/powiadomienia/edycja/' + notification.id)"
                  class="ml-2 mr-2"
                  :title="$t('ui.edit')">
                  <i class="material-icons">edit</i>
                </d-button>
                <d-button
                  theme="danger"
                  @click.native="removeNotification(notification.id)"
                  :disabled="!!removeInProgress"
                  :title="$t('ui.delete')">
                  <i class="material-icons">delete</i>
                </d-button>
              </td>
            </tr>

            <template v-for="(subnotification, subindex) of notification.children">
              <tr
                v-if="showChildrenNotifications[index]"
                :key="'notification-child-' + index + '-' + subindex"
                class="sub-notification">
                <td class="align-middle pl-4">
                  {{ subnotification.description }}
                  <template v-if="userIsAdmin && subnotification.user">
                    <br>
                    <small class="pt-2 d-block">
                      {{ $t('users.user') }}:
                      <strong>{{ subnotification.user.name }}</strong>
                    </small>
                    <template v-if="subnotification.order">
                      <small>
                        {{ $t('notifications.order') }}:
                        <strong>{{ subnotification.order.order_name }}</strong>
                      </small>
                    </template>
                  </template>
                </td>

                <td class="align-middle">
                  {{ subnotification.date }}<br>
                  {{ subnotification.time }}
                </td>

                <td class="align-middle">
                  <span
                    v-if="subnotification.is_repeatable"
                    class="text-success">
                    {{ $t('ui.yes') }}
                  </span>
                  <span
                    v-else
                    class="text-danger">
                    {{ $t('ui.no') }}
                  </span>

                  <small
                    v-if="subnotification.is_repeatable"
                    class="d-block">
                    {{ $t('notifications.form.frequencyValues.' + (subnotification.frequency).toString().toLowerCase()) }}<br>
                    <strong v-if="subnotification.repeat_number === 0">
                      &infin;
                    </strong>
                    <strong v-else>
                      {{ subnotification.repeat_number }}
                    </strong> {{ $t('notifications.times') }}
                  </small>
                </td>

                <td class="align-middle">
                  <span
                    v-if="subnotification.show_in_dashboard"
                    class="text-success">
                    {{ $t('ui.yes') }}
                  </span>
                  <span
                    v-if="!subnotification.show_in_dashboard"
                    class="text-danger">
                    {{ $t('ui.no') }}
                  </span>
                </td>

                <td class="align-middle">
                  <span
                    v-if="subnotification.send_reminder"
                    class="text-success">
                    {{ $t('ui.yes') }}
                  </span>
                  <span
                    v-if="!subnotification.send_reminder"
                    class="text-danger">
                    {{ $t('ui.no') }}
                  </span>
                </td>

                <td class="align-middle">
                  {{ parseInt(subnotification.reminder_before, 10) }} h
                </td>

                <td
                  class="align-middle"
                  style="width: 150px;">
                  <d-button
                    v-if="!subnotification.is_completed"
                    theme="success"
                    :outline="true"
                    @click.native="markAsCompleted(subnotification.id)"
                    :disabled="markingInProgress !== false"
                    :title="$t('notifications.markAsCompleted')">
                    <div
                      v-if="markingInProgress === subnotification.id"
                      class="spinner-border spinner-border-sm"
                      role="status"></div>
                    <i
                      v-else
                      class="material-icons">check</i>
                  </d-button>
                  <d-button
                    v-if="subnotification.is_completed"
                    theme="success"
                    @click.native="markAsNotCompleted(subnotification.id)"
                    :disabled="markingInProgress !== false"
                    :title="$t('notifications.markAsNotCompleted')">
                    <div
                      v-if="markingInProgress === subnotification.id"
                      class="spinner-border spinner-border-sm"
                      role="status"></div>
                    <i
                      v-else
                      class="material-icons">check</i>
                  </d-button>
                  <d-button
                    theme="primary"
                    @click.native="$router.push('/powiadomienia/edycja/' + subnotification.id)"
                    class="ml-2 mr-2"
                    :title="$t('ui.edit')">
                    <i class="material-icons">edit</i>
                  </d-button>
                  <d-button
                    theme="danger"
                    @click.native="removeNotification(subnotification.id)"
                    :disabled="!!removeInProgress"
                    :title="$t('ui.delete')">
                    <i class="material-icons">delete</i>
                  </d-button>
                </td>
              </tr>
            </template>
          </template>
          <tr v-if="notifications.length === 0">
            <td
              colspan="7"
              class="text-center">
              {{ $t('notifications.noNotificationsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!loaded"
        class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import ListUtils from './../utils/ListUtils.js';
import HasPagination from './../mixins/HasPagination.vue';
import Pagination from './../components/common/Pagination.vue';
import CalendarUtils from './../utils/CalendarUtils.js';
import cloneDeep from 'lodash.clonedeep';
import Vue from 'vue';

export default {
  name: 'notifications',
  props: {
    isOrderView: {
      default: false,
      type: Boolean
    }
  },
  mixins: [
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    },
    isDashboardList () {
      return !!this.$route.meta.showDashboardResults;
    },
    disableFiltersInSubview () {
      return this.isOrderView;
    },
    isCompletedOptions () {
      return [
        { text: this.$t('notifications.completedFilterAll'), value: -1 },
        { text: this.$t('notifications.completedFilterIsNotCompleted'), value: 0 },
        { text: this.$t('notifications.completedFilterIsCompleted'), value: 1 }
      ];
    },
    filtersConfig () {
      return [
        // BASIC FILTERS
        {
          key: 'is_completed',
          apiKey: 'is_completed',
          label: this.$t('notifications.completedFilterAll'),
          type: 'toggle',
          isBasic: true,
          defaultValue: -1,
          options: this.isCompletedOptions,
          apiTransform: (val, where) => {
            if (val !== -1) {
              where.is_completed = val;
            }
          }
        },
        {
          key: 'search',
          apiKey: 'search',
          label: this.$t('ui.search'),
          placeholder: this.$t('ui.enterSearchTerm'),
          type: 'text',
          isBasic: true,
          defaultValue: '',
          maxWidth: '25%'
        },
        {
          key: 'order_name',
          apiKey: 'order_name',
          label: this.$t('ui.orderName'),
          placeholder: this.$t('purchase.enterOrderName'),
          type: 'text',
          isBasic: true,
          defaultValue: '',
          maxWidth: '25%'
        },
        {
          key: 'dateRange',
          label: this.$t('ui.chooseDate'),
          type: 'daterange',
          isBasic: true,
          defaultValue: { startDate: null, endDate: null },
          maxWidth: '300px',
          apiTransform: (val, where) => {
            if (val.startDate) {
              where['date_from'] = CalendarUtils.formatDateForPHP(new Date(val.startDate));
            }
            if (val.endDate) {
              where['date_to'] = CalendarUtils.formatDateForPHP(new Date(val.endDate));
            }
          }
        },
        // ADVANCED FILTERS
        {
          key: 'customer_id',
          apiKey: 'customer_id',
          label: this.$t('users.customer'),
          type: 'customerSelect',
          isBasic: false,
          defaultValue: null
        },
        {
          key: 'potential_customer_id',
          apiKey: 'potential_customer_id',
          label: this.$t('potentialCustomers.potentialCustomerView'),
          type: 'customerSelect',
          customerType: 'potential',
          isBasic: false,
          defaultValue: null
        },
        {
          key: 'market_customer_id',
          apiKey: 'market_customer_id',
          label: this.$t('marketCustomers.marketCustomer'),
          type: 'customerSelect',
          customerType: 'market',
          isBasic: false,
          defaultValue: null
        },
        {
          key: 'show_in_dashboard',
          apiKey: 'show_in_dashboard',
          label: this.$t('notifications.showInDashboardFilter'),
          type: 'checkbox',
          isBasic: false,
          defaultValue: false,
          apiTransform: (val, where) => {
            if (val) {
              where.show_in_dashboard = 1;
            }
          }
        },
        {
          key: 'only_future',
          apiKey: 'only_future',
          label: this.$t('notifications.onlyFutureFilter'),
          type: 'checkbox',
          isBasic: false,
          defaultValue: false,
          apiTransform: (val, where) => {
            if (val) {
              where.only_future = 1;
            }
          }
        },
        {
          key: 'show_all',
          apiKey: 'show_all',
          label: this.$t('notifications.showAllFilter'),
          type: 'checkbox',
          isBasic: false,
          defaultValue: this.$store.getters.getUserType === 'admin',
          permission: () => this.userIsAdmin,
          apiTransform: (val, where) => {
            if (val && this.userIsAdmin) {
              where.show_all = 1;
            }
          }
        }
      ];
    }
  },
  data () {
    return {
      notifications: [],
      loaded: false,
      loadError: false,
      markingInProgress: false,
      removeInProgress: false,
      skipLSPagination: false,
      showChildrenNotifications: {},
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  created () {
    if (this.isOrderView) {
      this.skipLSPagination = true;
    }
  },
  mounted () {
    this.$bus.$on('notifications-list-reload-needed', this.debouncedLoadFilteredData);

    if (this.isDashboardList || this.isOrderView) {
      this.loadFilteredData();
    }
  },
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loaded = false;
      this.loadError = false;
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let whereData = cloneDeep(this.apiQueryPayload) || {};

      if (this.isDashboardList) {
        whereData = {
          is_near: 1
        };
      } else if (this.isOrderView) {
        whereData = {
          order_id: parseInt(this.$route.params.id, 10)
        };
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/notifications/items?page=' + this.currentPage,
        listField: 'notifications',
        loadError: 'loadError',
        params: {
          where: whereData
        },
        successAction: () => {
          Vue.set(this, 'showChildrenNotifications', {});
        }
      });
    },
    removeNotification (notificationID) {
      this.removeInProgress = notificationID;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/notifications/delete/',
        id: notificationID,
        successTitle: this.$t('notifications.removeNotificationSuccessTitle'),
        successText: this.$t('notifications.removeNotificationSuccessText'),
        confirmTitle: this.$t('notifications.removeNotificationConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('notifications.removeNotificationErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    markAsCompleted (notificationID) {
      this.markingInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/notifications/mark-completed/' + notificationID,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('notifications.markingFailedTryAgain'),
        formData: {
          is_completed: 1
        },
        successAction: () => {
          this.markingInProgress = false;
          this.loadFilteredData();
        },
        failAction: () => {
          this.markingInProgress = false;
        }
      });
    },
    markAsNotCompleted (notificationID) {
      this.markingInProgress = notificationID;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/notifications/mark-completed/' + notificationID,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('notifications.markingFailedTryAgain'),
        formData: {
          is_completed: 0
        },
        successAction: () => {
          this.markingInProgress = false;
          this.loadFilteredData();
        },
        failAction: () => {
          this.markingInProgress = false;
        }
      });
    },
    toggleChildrenNotifications (index) {
      if (this.showChildrenNotifications[index]) {
        Vue.set(this.showChildrenNotifications, index, false);
      } else {
        Vue.set(this.showChildrenNotifications, index, true);
      }
    }
  },
  beforeDestroy () {
    this.$bus.$off('notifications-list-reload-needed', this.debouncedLoadFilteredData);
  }
};
</script>

<style lang="scss" scoped>
.sub-notification {
  background-color: #f5f5f5;
}
</style>
